/*
 * TrackShareCard — Offscreen 1080×1350 (4:5 IG-friendly) share card.
 * Consumed by TrackShareModal. Uses the same bake pipeline as
 * NowFeaturedCard so every share surface renders through the identical
 * canvas → data URI path.
 *
 * Elements required by product spec:
 *   1. Track artwork (hero, full-bleed)
 *   2. Artist name
 *   3. Track title
 *   4. RGM Studio branding
 *   5. Track URL (visible on the card as a short label)
 */
import React, { forwardRef } from "react";

const TrackShareCard = forwardRef(({ data, bakedCover, bakedAvatar, preset = "square" }, ref) => {
  const cover = bakedCover || data.cover || "";
  const avatar = bakedAvatar || data.avatar || "";
  const title = data.track_title || "Untitled";
  const artist = data.artist_handle || data.artist_name || "";
  const shortUrl = (data.share_url || "").replace(/^https?:\/\//, "").replace(/^www\./, "");

  // Preset dimensions — reuses the existing baked pipeline; only the
  // frame size changes. ``square`` keeps the legacy 1080×1350 IG-post
  // shape. ``story`` and ``reel`` output the vertical 1080×1920 that
  // Instagram / Facebook Stories & Reels drafts accept.
  const dims = preset === "story" || preset === "reel"
    ? { w: 1080, h: 1920 }
    : { w: 1080, h: 1350 };
  const isVerticalPreset = preset === "story" || preset === "reel";
  const badge = preset === "reel" ? "🎬 REEL-READY" : (preset === "story" ? "📸 STORY-READY" : "🎧 TRACK");

  // ---- 9:16 Story / Reel composition (Feb 2026 rebuild) ------------
  // Intentional three-zone layout that fills the full 1080×1920 canvas
  // instead of a stretched 4:5 with a blank middle. Post 1:1 keeps its
  // original composition below (unchanged).
  //
  // Gated on `preset` — NOT on `dims.h > dims.w` — because the 4:5 post
  // preset is ALSO taller than wide (1350 > 1080). Story/Reel is the
  // only path that should enter this vertical layout.
  if (isVerticalPreset) {
    // IG-Stories safe zones: top 250 reserved (avatar/reactions),
    // bottom 320 reserved (sticker sheet). Keep content inside 250–1600.
    return (
      <div
        ref={ref}
        data-testid="track-share-card"
        style={{
          width: dims.w,
          height: dims.h,
          position: "relative",
          overflow: "hidden",
          fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
          color: "#fff",
          background:
            "radial-gradient(ellipse at 20% 0%, #3a1160 0%, #1a0b2e 45%, #07020d 100%)",
        }}
      >
        {/* Ambient blurred cover as background wash so the frame never
            reads as a flat gradient when the cover is present. */}
        {cover && (
          <img
            crossOrigin="anonymous"
            src={cover}
            alt=""
            aria-hidden
            style={{
              position: "absolute", inset: 0, width: "100%", height: "100%",
              objectFit: "cover", filter: "blur(48px) brightness(0.35) saturate(1.15)",
              transform: "scale(1.15)",
            }}
          />
        )}
        {/* Vignette overlay for legibility across bright covers */}
        <div style={{
          position: "absolute", inset: 0,
          background:
            "linear-gradient(180deg, rgba(7,2,13,0.55) 0%, rgba(7,2,13,0.15) 22%, rgba(7,2,13,0.15) 62%, rgba(7,2,13,0.9) 100%)",
        }} />

        {/* Zone 1 — Header (RGM branding + STORY/REEL pill), inside
            IG-Stories safe area. Top-anchored at y=260. */}
        <div style={{
          position: "absolute", top: 260, left: 72, right: 72,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{
            fontSize: 38, fontWeight: 900, letterSpacing: 6, color: "#ffcf5c",
          }}>
            RGM · STUDIO
          </div>
          <div style={{
            background: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,207,92,0.45)",
            borderRadius: 999, padding: "12px 26px",
            fontSize: 24, fontWeight: 800, letterSpacing: 2,
            color: "#ffcf5c",
          }}>
            {badge}
          </div>
        </div>

        {/* Zone 2 — Hero cover art panel. 780×780, centered horizontally,
            top-anchored at y=380 so it sits directly beneath the header. */}
        <div style={{
          position: "absolute", top: 380, left: 150, width: 780, height: 780,
          borderRadius: 44,
          overflow: "hidden",
          boxShadow:
            "0 40px 80px rgba(0,0,0,0.6), 0 0 0 6px rgba(255,207,92,0.35)",
          background: "linear-gradient(135deg, #4b0d70, #a3126e)",
        }}>
          {cover ? (
            <img
              crossOrigin="anonymous"
              src={cover}
              alt=""
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 200, fontWeight: 900, color: "rgba(255,255,255,0.4)",
            }}>
              ♪
            </div>
          )}
          {/* Subtle top gloss */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: "30%",
            background: "linear-gradient(180deg, rgba(255,255,255,0.14), rgba(255,255,255,0))",
          }} />
        </div>

        {/* Zone 3 — Info card. Top-anchored at y=1200 so it flows
            directly under the cover panel (which ends at y=1160). No
            empty middle band. Stays above the reserved bottom safe area. */}
        <div style={{
          position: "absolute", top: 1200, left: 72, right: 72,
          padding: "36px 44px",
          borderRadius: 32,
          background: "rgba(15,7,32,0.72)",
          border: "1px solid rgba(255,207,92,0.30)",
          backdropFilter: "blur(24px)",
          display: "flex", gap: 32, alignItems: "flex-start",
        }}>
          {avatar && (
            <img
              crossOrigin="anonymous"
              src={avatar}
              alt=""
              style={{
                width: 132, height: 132, borderRadius: "50%",
                border: "5px solid #ffcf5c", objectFit: "cover", flexShrink: 0,
              }}
            />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 28, fontWeight: 700, letterSpacing: 0.5,
              color: "#ffcf5c", marginBottom: 8,
            }}>
              {artist ? `@${artist.replace(/^@/, "")}` : "RGM Artist"}
            </div>
            <div style={{
              fontSize: 62, fontWeight: 900, lineHeight: 1.02,
              color: "#fff",
              textShadow: "0 3px 14px rgba(0,0,0,0.6)",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              marginBottom: 16,
            }}>
              {title}
            </div>
            {shortUrl && (
              <div style={{
                fontSize: 22, fontWeight: 700, letterSpacing: 1.2,
                color: "rgba(255,255,255,0.9)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
                ▶ {shortUrl}
              </div>
            )}
          </div>
        </div>

        {/* Zone 4 — bottom flourish placed ABOVE the reserved bottom
            safe area (y<1600). */}
        <div style={{
          position: "absolute", left: 0, right: 0, top: 1520,
          textAlign: "center",
          fontSize: 22, fontWeight: 800, letterSpacing: 8,
          color: "rgba(255,207,92,0.70)",
        }}>
          LISTEN · SHARE · SUPPORT
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid="track-share-card"
      style={{
        width: dims.w,
        height: dims.h,
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
        color: "#fff",
        background: "linear-gradient(180deg, #1a0b2e 0%, #0e0817 100%)",
      }}
    >
      {/* Cover — fills full frame */}
      {cover ? (
        <img
          crossOrigin="anonymous"
          src={cover}
          alt=""
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            objectFit: "cover", filter: "brightness(0.85)",
          }}
        />
      ) : (
        <div style={{ position: "absolute", inset: 0,
                      background: "linear-gradient(135deg, #4b0d70, #a3126e)" }} />
      )}

      {/* Bottom gradient overlay for text legibility */}
      <div style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: "60%",
        background: "linear-gradient(180deg, rgba(10,5,20,0) 0%, rgba(10,5,20,0.55) 30%, rgba(10,5,20,0.92) 90%)",
      }} />

      {/* Top-left branding */}
      <div style={{
        position: "absolute", top: 44, left: 48,
        fontSize: 22, fontWeight: 900, letterSpacing: 3,
        color: "#ffcf5c",
      }}>
        RGM · STUDIO
      </div>

      {/* Top-right pill */}
      <div style={{
        position: "absolute", top: 40, right: 44,
        background: "rgba(255,255,255,0.14)",
        border: "1px solid rgba(255,255,255,0.25)",
        borderRadius: 999, padding: "10px 20px",
        fontSize: 20, fontWeight: 800, letterSpacing: 1.5,
      }}>
        🎧 TRACK
      </div>

      {/* Bottom content stack */}
      <div style={{ position: "absolute", left: 56, right: 56, bottom: 88 }}>
        {avatar && (
          <img
            crossOrigin="anonymous"
            src={avatar}
            alt=""
            style={{
              width: 96, height: 96, borderRadius: "50%",
              border: "4px solid #ffcf5c", objectFit: "cover",
              marginBottom: 22,
            }}
          />
        )}
        <div style={{
          fontSize: 30, fontWeight: 700, letterSpacing: 0.5,
          color: "#ffcf5c", marginBottom: 10, textShadow: "0 2px 12px rgba(0,0,0,0.7)",
        }}>
          {artist ? `@${artist.replace(/^@/, "")}` : "RGM Artist"}
        </div>
        <div style={{
          fontSize: 70, fontWeight: 900, lineHeight: 1.05,
          textShadow: "0 4px 20px rgba(0,0,0,0.85)",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}>
          {title}
        </div>
        {shortUrl && (
          <div style={{
            marginTop: 28,
            fontSize: 22, fontWeight: 700, letterSpacing: 1.4,
            color: "rgba(255,255,255,0.85)",
          }}>
            ▶ {shortUrl}
          </div>
        )}
      </div>
    </div>
  );
});

TrackShareCard.displayName = "TrackShareCard";
export default TrackShareCard;
